<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;max-width:1200px;">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="st-page-title"><i class="mdi mdi-account-clock me-2"></i>Staff Attendance</h4>
        </div>
        <div class="d-flex gap-2">
          <input class="st-input" type="date" v-model="selectedDate" @change="loadAttendance" style="width:150px;padding:7px 10px;" />
          <button class="btn st-btn-primary btn-sm" @click="loadAttendance">🔄</button>
        </div>
      </div>

      <!-- Summary -->
      <div class="sa-grid" v-if="data">
        <div class="sa-card"><div class="sa-label">Total Staff</div><div class="sa-value">{{ data.total }}</div></div>
        <div class="sa-card"><div class="sa-label">Present</div><div class="sa-value" style="color:#10B981;">{{ data.present }}</div></div>
        <div class="sa-card"><div class="sa-label">Late</div><div class="sa-value" style="color:#F59E0B;">{{ data.late }}</div></div>
        <div class="sa-card"><div class="sa-label">Absent</div><div class="sa-value" style="color:#EF4444;">{{ data.absent }}</div></div>
        <div class="sa-card"><div class="sa-label">On Leave</div><div class="sa-value" style="color:#8B5CF6;">{{ data.on_leave }}</div></div>
      </div>

      <!-- Tabs -->
      <div class="la-tabs">
        <button class="la-tab" :class="{active:tab==='daily'}" @click="tab='daily'">📋 Daily</button>
        <button class="la-tab" :class="{active:tab==='report'}" @click="tab='report'; loadReport()">📊 Monthly Report</button>
        <button class="la-tab" :class="{active:tab==='cards'}" @click="tab='cards'; loadCards()">💳 Cards</button>
      </div>

      <!-- Daily Tab -->
      <div v-if="tab==='daily'" class="st-section" style="padding:14px;">
        <div class="d-flex gap-2 mb-3">
          <button class="btn st-btn-outline btn-sm" @click="bulkMark('present')">✅ All Present</button>
          <button class="btn st-btn-outline btn-sm" @click="bulkMark('absent')">❌ All Absent</button>
          <span style="flex:1;"></span>
          <button class="btn st-btn-primary btn-sm" @click="saveBulk" :disabled="saving"><span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>💾 Save All</button>
        </div>
        <div v-if="loading" class="st-spinner"><span class="spinner-border"></span></div>
        <table v-else class="la-table">
          <thead><tr><th>Name</th><th>Role</th><th>Check In</th><th>Check Out</th><th>Status</th><th>Notes</th></tr></thead>
          <tbody>
          <tr v-for="s in staffList" :key="s.id">
            <td><strong style="color:#E8EDF4;">{{ s.name }}</strong></td>
            <td style="text-transform:capitalize;color:#8BA4C4;">{{ s.role?.replace('_',' ') }}</td>
            <td>{{ s.check_in ? formatTime(s.check_in) : '—' }}</td>
            <td>{{ s.check_out ? formatTime(s.check_out) : '—' }}</td>
            <td>
              <select class="st-input" v-model="s._status" style="width:110px;padding:4px 6px;font-size:11px;">
                <option value="present">✅ Present</option>
                <option value="late">⏰ Late</option>
                <option value="absent">❌ Absent</option>
                <option value="leave">📝 Leave</option>
                <option value="half_day">½ Half Day</option>
              </select>
            </td>
            <td><input class="st-input" v-model="s._notes" placeholder="..." style="width:120px;padding:4px 6px;font-size:11px;" /></td>
          </tr>
          </tbody>
        </table>
        <div v-if="saveMsg" class="st-alert mt-2" :class="saveOk ? 'st-alert-success' : 'st-alert-danger'">{{ saveMsg }}</div>
      </div>

      <!-- Monthly Report -->
      <div v-if="tab==='report'" class="st-section" style="padding:14px;overflow-x:auto;">
        <div class="d-flex gap-2 mb-3 align-items-end">
          <div><label class="st-label" style="font-size:10px;">Month</label><input class="st-input" type="month" v-model="reportMonth" style="width:160px;" /></div>
          <button class="btn st-btn-primary btn-sm" @click="loadReport">📊 Load</button>
          <button class="btn st-btn-outline btn-sm" @click="exportReport">📄 PDF</button>
        </div>
        <div v-if="reportLoading" class="st-spinner"><span class="spinner-border"></span></div>
        <table v-else-if="report.length" class="la-table">
          <thead><tr><th>Name</th><th>Role</th><th>Present</th><th>Late</th><th>Absent</th><th>Leave</th><th>Half Day</th><th>Avg Late (min)</th></tr></thead>
          <tbody>
          <tr v-for="r in report" :key="r.id">
            <td><strong style="color:#E8EDF4;">{{ r.name }}</strong></td>
            <td style="color:#8BA4C4;text-transform:capitalize;">{{ r.role?.replace('_',' ') }}</td>
            <td style="color:#10B981;font-weight:600;">{{ r.present || 0 }}</td>
            <td style="color:#F59E0B;">{{ r.late || 0 }}</td>
            <td style="color:#EF4444;font-weight:600;">{{ r.absent || 0 }}</td>
            <td style="color:#8B5CF6;">{{ r.on_leave || 0 }}</td>
            <td>{{ r.half_day || 0 }}</td>
            <td>{{ Math.round(r.avg_late_minutes || 0) }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards Tab -->
      <div v-if="tab==='cards'" class="st-section" style="padding:14px;">
        <div v-if="cardsLoading" class="st-spinner"><span class="spinner-border"></span></div>
        <table v-else class="la-table">
          <thead><tr><th>Name</th><th>Role</th><th>Card</th><th>Wallet Balance</th><th>Action</th></tr></thead>
          <tbody>
          <tr v-for="s in staffCards" :key="s.id">
            <td><strong style="color:#E8EDF4;">{{ s.name }}</strong></td>
            <td style="color:#8BA4C4;text-transform:capitalize;">{{ s.role?.replace('_',' ') }}</td>
            <td><span v-if="s.has_card == 1" style="color:#10B981;">✅ Assigned</span><span v-else style="color:#4A6A8A;">No card</span></td>
            <td>KSh {{ Number(s.wallet_balance || 0).toLocaleString() }}</td>
            <td><button class="btn st-btn-outline btn-sm" style="padding:3px 10px;font-size:11px;" @click="registerCard(s)">{{ s.has_card == 1 ? '🔄 Re-assign' : '💳 Assign Card' }}</button></td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'StaffAttendance',
  components: { Navigation },
  data() {
    const ld = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0'); };
    const lm = () => { const d = new Date(); return d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0'); };
    return {
      tab: 'daily', selectedDate: ld(),
      data: null, staffList: [], loading: false,
      saving: false, saveMsg: '', saveOk: false,
      reportMonth: lm(), report: [], reportLoading: false,
      staffCards: [], cardsLoading: false,
    };
  },
  mounted() { this.loadAttendance(); },
  methods: {
    formatTime(dt) { if (!dt) return ''; const p = dt.split(' ')[1]?.split(':'); if (!p) return ''; const h = +p[0]; return `${h%12||12}:${p[1]} ${h>=12?'PM':'AM'}`; },
    async loadAttendance() {
      this.loading = true;
      try {
        const res = await executeJson({ function: 'getStaffAttendance', date: this.selectedDate }, simitrack);
        if (res.data.success) {
          this.data = res.data.data;
          this.staffList = (res.data.data?.staff || []).map(s => ({ ...s, _status: s.status || 'absent', _notes: s.notes || '' }));
        }
      } catch(e) {}
      this.loading = false;
    },
    bulkMark(status) { this.staffList.forEach(s => s._status = status); },
    async saveBulk() {
      this.saving = true; this.saveMsg = '';
      const records = this.staffList.map(s => ({ userId: s.id, status: s._status, notes: s._notes }));
      try {
        const res = await executeJson({ function: 'bulkMarkStaffAttendance', date: this.selectedDate, records: JSON.stringify(records) }, simitrack);
        this.saveMsg = res.data.message; this.saveOk = res.data.success;
        if (res.data.success) this.loadAttendance();
      } catch(e) { this.saveMsg = 'Error'; }
      this.saving = false;
    },
    async loadReport() {
      this.reportLoading = true;
      try {
        const res = await executeJson({ function: 'getStaffAttendanceReport', month: this.reportMonth }, simitrack);
        if (res.data.success) this.report = res.data.data?.report || [];
      } catch(e) {}
      this.reportLoading = false;
    },
    async loadCards() {
      this.cardsLoading = true;
      try {
        const res = await executeJson({ function: 'getStaffCards' }, simitrack);
        if (res.data.success) this.staffCards = res.data.data || [];
      } catch(e) {}
      this.cardsLoading = false;
    },
    async registerCard(staff) {
      const uid = prompt(`Tap card on reader for ${staff.name}, then paste the UID here:`);
      if (!uid) return;
      try {
        const res = await executeJson({ function: 'registerStaffCard', userId: staff.id, rfidUid: uid.trim() }, simitrack);
        alert(res.data.message);
        this.loadCards();
      } catch(e) { alert('Error'); }
    },
    exportReport() {
      if (!this.report.length) return;
      const inst = sessionStorage.getItem('institutionName') || '';
      let html = `<!DOCTYPE html><html><head><title>Staff Attendance</title><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Segoe UI',Arial,sans-serif;font-size:11px;padding:15mm}@page{size:A4;margin:12mm}h1{font-size:18px;color:#0D2B5E}h2{font-size:14px;color:#2E86DE;margin:4px 0 12px}table{width:100%;border-collapse:collapse;margin-top:8px}th{background:#0D2B5E;color:#fff;padding:5px 8px;font-size:10px;text-align:left}td{padding:4px 8px;border-bottom:1px solid #e0e0e0;font-size:10px}tr:nth-child(even){background:#f9fbfd}.footer{margin-top:16px;border-top:1px solid #ddd;padding-top:8px;font-size:9px;color:#999}</style></head><body>`;
      html += `<h1>${inst}</h1><h2>Staff Attendance Report — ${this.reportMonth}</h2>`;
      html += '<table><tr><th>Name</th><th>Role</th><th>Present</th><th>Late</th><th>Absent</th><th>Leave</th><th>Half Day</th></tr>';
      this.report.forEach(r => { html += `<tr><td>${r.name}</td><td>${r.role||''}</td><td>${r.present||0}</td><td>${r.late||0}</td><td>${r.absent||0}</td><td>${r.on_leave||0}</td><td>${r.half_day||0}</td></tr>`; });
      html += `</table><div class="footer">${inst} — Simi Trac · Generated ${new Date().toLocaleString()}</div></body></html>`;
      const w = window.open('','_blank'); w.document.write(html); w.document.close(); setTimeout(()=>w.print(),500);
    },
  }
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;}.st-back:hover{color:#4FC3F7;}.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;padding:20px;margin-bottom:16px;}.st-label{color:#8BA4C4;font-size:12px;font-weight:600;margin-bottom:4px;display:block;}
.st-input{width:100%;background:#0A1628;border:1px solid #1A3A6A;border-radius:8px;padding:8px 12px;color:#E8EDF4;font-size:13px;outline:none;}.st-input:focus{border-color:#2E86DE;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}.st-btn-outline:hover{border-color:#4FC3F7;color:#4FC3F7;}
.st-alert{padding:10px 14px;border-radius:10px;font-size:13px;}.st-alert-success{background:rgba(16,185,129,.1);border:1px solid rgba(16,185,129,.2);color:#6EE7B7;}.st-alert-danger{background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.2);color:#EF4444;}
.st-spinner{display:flex;justify-content:center;padding:30px;}.st-spinner .spinner-border{color:#2E86DE;}
.sa-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(130px,1fr));gap:10px;margin-bottom:16px;}.sa-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:10px;padding:12px;text-align:center;}.sa-label{font-size:10px;color:#4A6A8A;text-transform:uppercase;font-weight:600;}.sa-value{font-size:24px;font-weight:800;}
.la-tabs{display:flex;gap:4px;margin-bottom:16px;border-bottom:2px solid #1A3A6A;}.la-tab{background:none;border:none;padding:10px 16px;color:#4A6A8A;font-size:13px;font-weight:600;cursor:pointer;border-bottom:2px solid transparent;margin-bottom:-2px;}.la-tab.active{color:#4FC3F7;border-bottom-color:#4FC3F7;}
.la-table{width:100%;border-collapse:collapse;font-size:12px;color:#E8EDF4;}.la-table th{background:#0A1628;padding:6px 8px;color:#4FC3F7;font-size:10px;text-transform:uppercase;border-bottom:2px solid #1A3A6A;}.la-table td{padding:5px 8px;border-bottom:1px solid #1A3A6A;}
</style>